import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import React, { Fragment, useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { RadioButtons } from "../..";
import { CheckedStates } from "../../Checkbox";
import { useFormContext } from "../../Form";
import { StatusLabel } from "../../StatusLabel";
import { getBoolValueFromRow } from "../../table/v2/utilsV2Table";
import { useIconClasses } from "../../theming/icons";
import { setIcon } from "../../utils/icons";
export const SetRadioButton = observer(({ options, name, dataId, dataTestId }) => {
    const { values } = useFormContext();
    const value = toJS(values.get(name));
    const option = options?.filter((option) => option.value === value);
    if (option && option[0]) {
        return (React.createElement(StatusLabel, { text: option[0].label, color: option[0].readColor, dataId: dataId, dataTestId: dataTestId }));
    }
    return React.createElement("span", null, "-");
});
export const CheckedState = observer(({ name }) => {
    const { values } = useFormContext();
    const set = toJS(values.get(name));
    let { iconType, iconValue } = setIcon("check");
    const iconClasses = useIconClasses(iconType);
    if (set) {
        return (React.createElement(Fragment, null,
            React.createElement("span", { className: `${iconClasses.icon} ${iconType} ${iconType}-${iconValue}` }),
            React.createElement("span", { className: "sr-only" }, "Checked")));
    }
    return React.createElement("span", null, "-");
});
export const BulkCheckbox = observer(({ name, formComponent, dataTestId, dataId, label }) => {
    const { id: columnKey, onBulkChange, disabled, hidden } = formComponent;
    const { values } = useFormContext();
    const classes = useClasses();
    const collection = toJS(values.get(name)) || [];
    const handleChange = (e) => {
        let value = collection || [];
        onBulkChange && onBulkChange(e);
        value = value.map((item) => {
            const checkboxCanBeSet = !(getBoolValueFromRow(item, disabled) || getBoolValueFromRow(item, hidden));
            if (checkboxCanBeSet) {
                const updatedValue = (item[columnKey] = e.target.checked);
                return { ...item, updatedValue };
            }
            return item;
        });
        values.set(name, value);
    };
    const someRowsAreInCollection = () => collection.filter((item) => !!item[columnKey]);
    const allRowsAreInCollection = () => collection.every((item) => item[columnKey] || getBoolValueFromRow(item, hidden));
    const allRowsAreDisabled = () => collection.every((item) => getBoolValueFromRow(item, disabled) ||
        getBoolValueFromRow(item, hidden));
    const getCurrentState = () => {
        if (allRowsAreInCollection()) {
            return CheckedStates.Checked;
        }
        if (someRowsAreInCollection().length >= 1) {
            return CheckedStates.Indeterminate;
        }
        return CheckedStates.Unchecked;
    };
    const [checked, setChecked] = useState(getCurrentState());
    useEffect(() => setChecked(getCurrentState()), [collection]);
    return (React.createElement("label", null,
        React.createElement("span", { className: label === undefined ? "sr-only" : classes.label }, label ?? "Select All"),
        React.createElement("input", { name: `${name}_bulk`, type: "checkbox", onChange: handleChange, checked: checked === CheckedStates.Checked, disabled: allRowsAreDisabled(), ref: (el) => el && (el.indeterminate = checked === CheckedStates.Indeterminate), "data-testid": dataTestId && `${dataTestId}-${checked}`, "data-id": dataId && `${dataId}-${checked}` })));
});
export const useClasses = () => {
    const useStyles = createUseStyles({
        label: {
            display: "block",
        },
    });
    return useStyles();
};
export const BulkRadioButtons = observer(({ name, formComponent, label }) => {
    const { id: columnKey, onBulkChange, disabled, hidden, options, } = formComponent;
    const componentName = `${name}_bulk`;
    const { values } = useFormContext();
    const collection = toJS(values.get(name)) || [];
    const handleChange = (option) => {
        let value = collection;
        value = collection.map((item) => {
            const checkboxCanBeSet = !(getBoolValueFromRow(item, disabled) || getBoolValueFromRow(item, hidden));
            if (checkboxCanBeSet) {
                const updatedValue = (item[columnKey] = option);
                return { ...item, updatedValue };
            }
            return item;
        });
        onBulkChange && onBulkChange(option);
        values.set(name, value);
    };
    const allRowsAreInCollection = () => collection.every((item) => item[columnKey] === collection[0][columnKey]);
    const getCurrentState = () => {
        let currentState = undefined;
        if (allRowsAreInCollection()) {
            currentState = collection[0][columnKey];
        }
        values.set(componentName, currentState);
    };
    useEffect(() => getCurrentState(), [collection]);
    return (React.createElement(RadioButtons, { label: label, name: componentName, type: "button", options: options, onChange: handleChange }));
});
